import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import BarcodeScanner from './BarcodeScanner';

interface SerialSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string, searchType: 'serial' | 'invoice') => void;
  searchLoading?: boolean;
  title?: string;
}

const SerialSearchModal: React.FC<SerialSearchModalProps> = ({
  isOpen,
  onClose,
  onSearch,
  searchLoading = false,
  title = 'Talep Ara'
}) => {
  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'serial' | 'invoice'>('serial');
  
  // Barkod okuyucu modal state
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    setSearchQuery('');
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });



  const handleSearch = () => {
    if (searchQuery.trim()) {
      onSearch(searchQuery.trim(), searchType);
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClose = () => {
    handleModalClose();
  };

  // Barkod okuyucu handler'ları
  const handleBarcodeScanned = (result: string) => {
    setSearchQuery(result);
    setIsBarcodeScannerOpen(false);
  };

  const handleBarcodeScanError = (error: string) => {
    console.error('Barkod okuma hatası:', error);
  };

  const handleAutoCheck = () => {
    // Otomatik arama yap
    if (searchQuery.trim()) {
      handleSearch();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex flex-col justify-end md:justify-center bg-black/20 dark:bg-black/40 backdrop-blur-sm modal-backdrop-optimized"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              type: 'tween', 
              ease: [0.32, 0.72, 0, 1], // Custom easing for better performance
              duration: 0.25,
              opacity: { duration: 0.2 }
            }}
            className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-2xl p-4 pt-6 flex flex-col max-h-[70vh] md:max-h-[75vh] modal-optimized"
            style={{ 
              minHeight: 280,
              willChange: 'transform, opacity',
              transform: 'translateZ(0)' // Force GPU acceleration
            }}
            onClick={e => e.stopPropagation()}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, info) => {
              // Sadece aşağıya doğru 150px'den fazla kaydırıldığında kapat
              if (info.offset.y > 150) {
                handleClose();
              }
            }}
          >
            {/* Drag handle */}
            <div className="flex flex-col mb-4 flex-shrink-0 cursor-grab active:cursor-grabbing">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>
              
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 border border-blue-200 dark:border-blue-700 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
                {title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Seri numarası veya fatura numarasına göre arama yapın
              </p>
            </div>

            {/* Content */}
            <div className="flex-1">
              {/* Search Type Selection */}
              <div className="mb-4">
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setSearchType('serial')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      searchType === 'serial'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Seri No
                  </button>
                  <button
                    onClick={() => setSearchType('invoice')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      searchType === 'invoice'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Fatura No
                  </button>
                </div>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={searchType === 'serial' ? 'Seri numarasını girin...' : 'Fatura numarasını girin...'}
                    className={`form-input w-full ${searchType === 'serial' ? (searchQuery ? 'pr-16' : 'pr-10') : (searchQuery ? 'pr-10' : 'pr-3')} text-base bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500`}
                  />
                  
                  {/* Barkod okuyucu butonu - sadece seri numarası seçiliyken göster */}
                  {searchType === 'serial' && (
                    <button
                      type="button"
                      onClick={() => setIsBarcodeScannerOpen(true)}
                      className={`absolute ${searchQuery ? 'right-9' : 'right-3'} top-1/2 -translate-y-1/2 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700`}
                      >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                        <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                        <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                        <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                        <rect x="7" y="7" width="10" height="10"/>
                        <rect x="9" y="9" width="6" height="6"/>
                      </svg>
                    </button>
                  )}
                  
                  {searchQuery && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none flex items-center"
                      onClick={() => setSearchQuery('')}
                      tabIndex={-1}
                      aria-label="Temizle"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Action Button */}
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={handleClose}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  <div className="rounded-lg border border-white dark:border-gray-700 px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">
                    İptal
                  </div>
                </button>
                <button
                  onClick={handleSearch}
                  disabled={!searchQuery.trim() || searchQuery.trim().length < 2 || searchLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 text-white font-medium rounded-lg shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-lg disabled:shadow-gray-400/25 disabled:hover:shadow-lg disabled:hover:shadow-gray-400/25 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                >
                  {searchLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Aranıyor...
                    </div>
                  ) : (
                    'Ara'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Barkod Okuyucu Modal */}
      <BarcodeScanner
        isOpen={isBarcodeScannerOpen}
        onClose={() => setIsBarcodeScannerOpen(false)}
        onScan={handleBarcodeScanned}
        onError={handleBarcodeScanError}
        onAutoCheck={handleAutoCheck}
      />
    </AnimatePresence>
  );
};

export default SerialSearchModal; 